import { useRef, useState } from 'react'
import { IconButton, Popover } from '@w7/ui'
import { Amount, Money } from '@/app/bits'
import { EMPTY } from '@/lib/format'
import { ROLE_LABEL, type Role } from '@/features/hierarchy/hierarchy'
import type { PlStatementRowView } from '@/api/map/plStatement'

/**
 * Lab PLStatement (i) dropdown — win/loss, turnover, commission, net, and this
 * ladder seat’s share. One popover per cell so the number and its breakdown
 * stay together.
 */
export function PlStatementSplitCell({
  row,
  role,
  currency,
}: {
  row: PlStatementRowView
  role: Role | 'member'
  currency?: string
}) {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement>(null)
  const amount = role === 'member' ? row.memberPnl : (row.split[role] ?? 0)
  const label =
    role === 'member' ? row.memberName : (row.splitNames[role] ?? EMPTY)

  return (
    <div className="w7-pl-split">
      <div className="w7-pl-split-name" title={label}>
        {label || EMPTY}
      </div>
      <div className="w7-pl-split-amt">
        {/* Lab: balance-up / balance-down by sign — no invert. */}
        <Money value={amount} signed currency={currency} />
        <IconButton
          ref={anchor}
          name="info"
          size="sm"
          label={`Breakdown for ${label}`}
          onClick={(event) => {
            event.stopPropagation()
            setOpen((v) => !v)
          }}
        />
      </div>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        label={`${label} P&L breakdown`}
        align="end"
        width={280}
      >
        <dl className="w7-pl-pop">
          <div>
            <dt>Member P&L</dt>
            <dd>
              <Money value={row.memberPnl} signed currency={currency} />
            </dd>
          </div>
          <div>
            <dt>Turnover</dt>
            <dd>
              <Amount value={row.turnover} currency={currency} />
            </dd>
          </div>
          <div>
            <dt>Commission</dt>
            <dd>
              <Amount value={row.commission} currency={currency} />
            </dd>
          </div>
          <div>
            <dt>Net to book</dt>
            <dd>
              <Money value={-row.netAmount} signed currency={currency} />
            </dd>
          </div>
          {role !== 'member' ? (
            <div>
              <dt>{ROLE_LABEL[role]} net</dt>
              <dd>
                <Money value={amount} signed currency={currency} />
              </dd>
            </div>
          ) : null}
        </dl>
      </Popover>
    </div>
  )
}
